/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {makeStyles, TableCell, TableRow} from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJobTasksElem} from '@ion/core/src/types/entities';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {dependentTaskColors} from '../helpers';

interface SortableTaskProps {
  jobTasks: DownloadJobTasksElem[];
  item: DownloadJobTasksElem;
  index: number;
  renderContent: Function;
  isOutsideParent?: boolean;
  isBelowChild?: boolean;
  activeIndex?: number | null;
  colorIndex?: number | null;
}

enum ArrowLine {
  WIDTH = 10,
  LEFT = -11,
  SPACE_BETWEEN = 5
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const colors = dlsAlt('colors', 'assets');
  return ({
    tableRow : {
      position              : 'relative',
      boxShadow             : `0px 2px 0px 0px ${dlsColor('grey6')}`,
      '& td .MuiInput-root' : {
        backgroundColor : 'transparent !important',
        '&::before'     : {
          borderBottom : `1px solid ${dlsColor('grey4')} !important`
        }
      },
      '& td .MuiFormControl-root > div' : {
        backgroundColor : 'transparent !important'
      }
    },
    dragIconCell : {
      width   : '0px !important',
      padding : '0px 8px !important',
      '& svg' : {
        color     : dlsColor('grey3'),
        cursor    : 'grab',
        padding   : '10px 0',
        '&:hover' : {
          color : dlsColor('grey1')
        }
      }
    },
    onDragging : {
      boxShadow       : '0 10px 15px -3px rgb(0, 0, 0, 0.1), 0 4px 6px -4px rgb(0, 0, 0, 0.1)',
      backgroundColor : dlsColor('white'),
      zIndex          : 999
    },
    outsideParentWarning : {
      backgroundColor       : `${colors.lightRed}!important`,
      outline               : '1px solid rgb(226, 35, 26, 0.47)',
      '& td .MuiInput-root' : {
        color : dlsColor('black')
      }
    },
    connectorParent : {
      width           : ArrowLine.WIDTH,
      height          : '0',
      display         : 'block',
      border          : `1px solid ${dlsColor('caBlue')}`,
      borderRight     : 'none',
      borderBottom    : 'none',
      position        : 'absolute',
      left            : ArrowLine.LEFT,
      top             : 'calc(50% + 4px)',
      transformOrigin : '0 0',
      animation       : `$lineAnim 0.2s ${theme.transitions.easing.easeIn}`
    },
    connectorChild : {
      width           : ArrowLine.WIDTH,
      height          : '1px',
      display         : 'block',
      backgroundColor : dlsColor('caBlue'),
      position        : 'absolute',
      left            : ArrowLine.LEFT,
      top             : 'calc(50% - 4px)',
      transformOrigin : '0 0',
      animation       : `$arrowAnim 0.25s ${theme.transitions.easing.easeIn}`,
      '&::before'     : {
        content         : '""',
        width           : '10px',
        height          : '7px',
        position        : 'absolute',
        top             : '-3px',
        right           : 0,
        backgroundColor : 'inherit',
        clipPath        : 'polygon(0 38%,42% 38%,36% 0,100% 50%,36% 100%,42% 62%,0 62%)'
      }
    },
    '@keyframes lineAnim' : {
      '0%' : {
        transform : 'scaleY(0)'
      },
      '100%' : {
        transform : 'scaleY(1)'
      }
    },
    '@keyframes arrowAnim' : {
      '0%' : {
        transform : 'scaleX(0)'
      },
      '100%' : {
        transform : 'scaleX(1)'
      }
    }
  });
});

/**
 * Defines the SortableTask component. This component displays an individual task or a
 * list of tasks. It can render each task as a list item using the renderContent function.
 * 
 * ```typescript
 * 
 *  import SortableTask from './dependentTaskDnd/SortableTask';
 * 
 *  <SortableTask key={`job-tasks-${item.id}`}
 *    jobTasks={jobTasks}
 *    item={item}
 *    index={index}
 *    renderContent={renderContent} />
 * 
 * ```
 */
export const SortableTask = ({
  item,
  index,
  jobTasks,
  renderContent,
  colorIndex = null,
  activeIndex = null,
  isBelowChild = false,
  isOutsideParent = false,
  ...props
}: SortableTaskProps) => {
  const {id: itemId, ancestorIds} = item || {};
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({id: itemId, data: {ancestorIds}});
  
  const {task, trackLine} = item || {};
  const parentIndex       = task?.metaData?.dependency?.index;
  const parentTask        = jobTasks[parentIndex];
  const hasChildren       = task?.children?.length > 0;
  const classes           = useStyles({trackLine, parentTask});
  
  const style = {
    backgroundColor : activeIndex === index ? dependentTaskColors(colorIndex)?.light : dlsColor('white'),
    transform       : CSS.Transform.toString(transform),
    transition
  };
  const isDisableDragging = (
    isUndefined(task?.fileType) ||
    task?.deviceType === '' ||
    task?.fileType === '' ||
    task?.fileName === ''
  );

  const allowListeners                                          = !isDisableDragging ? {...listeners} : undefined;
  const {tooltipText, outsideParentWarning, insideChildWarning} = translate('jobcreate:dependentTask', {
    returnObjects : true,
    dependentTask : parentIndex+1
  });
  const tooltipMessage = (isOutsideParent && isDragging)
    ? (isBelowChild ? insideChildWarning : outsideParentWarning)
    : tooltipText;

  return (
    <TableRow ref={setNodeRef}
      data-task-id={itemId}
      style={style}
      className={`
        ${classes.tableRow}
        ${isDragging && classes.onDragging}
        ${(isOutsideParent && isDragging) && classes.outsideParentWarning}
      `} >
      <Render.If condition={hasChildren}>
        <span className={classes.connectorParent}
          style={{
            width       : ArrowLine.WIDTH + Number(trackLine?.number * ArrowLine.SPACE_BETWEEN),
            left        : ArrowLine.LEFT + Number(trackLine?.number * -ArrowLine.SPACE_BETWEEN),
            height      : `calc((100% * ${trackLine?.endIndex - trackLine?.startIndex}) - 8px)`,
            borderColor : trackLine?.color
          }} />
      </Render.If>
      <Render.If condition={!isUndefined(parentIndex)}>
        <span className={classes.connectorChild}
          style={{
            width           : ArrowLine.WIDTH + Number(parentTask?.trackLine?.number * ArrowLine.SPACE_BETWEEN),
            left            : ArrowLine.LEFT + Number(parentTask?.trackLine?.number * -ArrowLine.SPACE_BETWEEN),
            backgroundColor : parentTask?.trackLine?.color
          }} />
      </Render.If>
      <Render.If condition={!hasChildren && isUndefined(parentIndex)}>
        <span />
      </Render.If>
      <TableCell className={classes.dragIconCell} ref={setActivatorNodeRef} {...attributes} {...allowListeners}>
        <Tooltip arrow placement={'bottom'}
          disableHoverListener={isDragging ? !isOutsideParent : isUndefined(parentIndex)}
          title={<span>{tooltipMessage}</span>}>
          <DragIndicatorIcon color={dlsColor('grey8')} />
        </Tooltip>
      </TableCell>
      {renderContent?.(item, index) || translate('jobcreate:dependentTask.noTaskAvailable')}
    </TableRow>
  );
};

export default SortableTask;
